import React from 'react';
import { Box, TextField, FormControl, InputLabel, Select, MenuItem, Stack } from '@mui/material';
import PortalDatePicker from 'components/PortalDatePicker';
import { useFormik } from 'formik';
import { query, collection, where, getDocs, DocumentSnapshot } from 'firebase/firestore';
import { firestoreInstance } from 'config/ApiService';
import { USERS } from 'core/utils/collectionNames';
import { LoadingButton } from '@mui/lab';
import { useSelector } from 'react-redux';
import { AGREEMENT_TYPE } from 'components/ClinicalServicesDropdown';
import { object, string } from 'yup';
import { useSnackbar } from 'notistack';
import { RootState } from 'redux/reducers';
import dayjs, { Dayjs } from 'dayjs';
import approveForSelfManagement from 'core/domain/usecases/approveForSelfManagement';
import CustomerSelect from './CustomerSelect';
import { logGoogleAnalyticsEvent } from 'utils/gaLog';

interface Programme {
    name: string;
    id: string;
}

interface Props {
    onSubmitted: () => void;
}

type LocalUserType = {
    client_id: string;
    first_name: string;
    last_name: string;
    email: string;
    role: string;
    status: string;
    external: boolean;
    id: string;
};

const REFERRAL_SCHEMA = object({
    first_name: string().min(2, 'Too short').required('First name is required'),
    last_name: string().required('Last name is required'),
    email: string().email('Invalid email').required('Email is required'),
    date_of_birth: string().required('date of birth is required'),
    programme_id: string().required('Programme is required'),
    clinician: string().required('Clinician is required'),
    postcode: string(),
    notes: string()
});

const DirectReferralForm = (props: Props) => {
    const { onSubmitted } = props;
    const [programmes, setProgrammes] = React.useState<Programme[]>([]);
    const [clinicianList, setClinicianList] = React.useState<Array<LocalUserType>>([]);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [ethemeralCustomer, setEthemeralCustomer] = React.useState<{
        customerId: string;
        customerName: string;
    } | null>(null);

    const permissions = useSelector((state: RootState) => state.AuthReducer.permissions);
    const loggedInClinician = useSelector(
        (state: { AuthReducer: { user: { id: string; email: string } } }) => state.AuthReducer.user
    );
    const userPermissions = useSelector((state: RootState) => state.AuthReducer.permissions);
    const region = useSelector((state: RootState) => state.AuthReducer.currentRegion);

    const { enqueueSnackbar } = useSnackbar();

    const isInternalClinicalServices = permissions.includes('internal_clinical_services');

    const client = useSelector(
        (state: {
            AuthReducer: {
                organisation: {
                    id: string;
                    clinical_services_enabled: boolean;
                    agreement_type: AGREEMENT_TYPE;
                    name: string;
                };
            };
        }) => state.AuthReducer.organisation
    );

    const isEqualOrAboveMinimumAge = (date: Date): boolean => {
        const age = dayjs().diff(date, 'year');
        return age >= 16 && age <= 100;
    };

    const formik = useFormik({
        initialValues: {
            first_name: '',
            last_name: '',
            date_of_birth: dayjs(new Date(Date.now())).format('MM/dd/yyyy'),
            email: '',
            postcode: '',
            programme_id: '',
            clinician: '',
            notes: ''
        },
        validationSchema: REFERRAL_SCHEMA,
        validate: (values) => {
            const errors: { [propName: string]: any } = {};
            if (!values.date_of_birth) {
                errors.date_of_birth = 'Required';
            } else if (!isEqualOrAboveMinimumAge(new Date(values.date_of_birth))) {
                errors.date_of_birth = 'Must be 16 years or older';
            }
            if (!values.email) {
                errors.email = 'Required';
            } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)) {
                errors.email = 'Invalid email address';
            }
            return errors;
        },
        onSubmit: async (values) => {
            try {
                setLoading(true);
                const assignedClinician = clinicianList.find(
                    (user) => user.id === values.clinician
                );
                if (isInternalClinicalServices && !ethemeralCustomer) {
                    enqueueSnackbar('Please select a customer', { variant: 'warning' });
                    setLoading(false);
                    return;
                }
                const customerID = isInternalClinicalServices
                    ? ethemeralCustomer?.customerId || ''
                    : client.id;
                const customerName = isInternalClinicalServices
                    ? ethemeralCustomer?.customerName || ''
                    : client.name;
                const requestPayload = {
                    ...values,
                    customer_id: customerID,
                    customer_name: customerName,
                    date_of_birth: dayjs(values.date_of_birth).format('YYYY-MM-DD'),
                    invitation_type: 'DIRECT_REFERRAL',
                    region,
                    clinician: {
                        name: `${assignedClinician?.first_name} ${assignedClinician?.last_name}`,
                        email: assignedClinician?.email,
                        id: assignedClinician?.id
                    },
                    triage_notes: values.notes
                };
                await approveForSelfManagement(requestPayload);
                logGoogleAnalyticsEvent('enrolled_direct_referral_submit', {
                    customer_id: customerID,
                    customer_name: customerName,
                    logged_in_user_id: assignedClinician?.id,
                    logged_in_username: `${assignedClinician?.first_name} ${assignedClinician?.last_name}`,
                    region,
                    logged_in_user_role: assignedClinician?.role
                });
                setLoading(false);
                enqueueSnackbar('Patient added successfully', { variant: 'success' });
                if (onSubmitted) {
                    onSubmitted();
                }
            } catch (e) {
                setLoading(false);
                const error = e as Error;
                enqueueSnackbar(error.message, { variant: 'error' });
            }
        }
    });

    const handleDateOfBirthChange = (date: Dayjs | null) => {
        if (!dayjs(date).isValid() || !date) {
            formik.setFieldValue('date_of_birth', '');
            return;
        }
        formik.setFieldValue('date_of_birth', dayjs(date).format('MM/DD/YYYY'));
    };

    // Fetch all programmes currently available
    React.useEffect(() => {
        const ref = query(
            collection(firestoreInstance(), 'phio_engage_programmes_v2'),
            where('status', '==', 'published'),
            where('state', '==', 'active')
        );
        getDocs(ref).then((snapshots) => {
            const programmeArray: Programme[] = [];
            snapshots.forEach((doc: DocumentSnapshot) => {
                const data = doc.data() || { name: '' };
                programmeArray.push({
                    name: data?.name,
                    id: doc.id
                });
            });
            setProgrammes(programmeArray);
        });
    }, []);

    // Fetch current clinicians
    React.useEffect(() => {
        (async () => {
            const ref = collection(firestoreInstance(), USERS);

            const q = userPermissions.includes('internal_clinical_services')
                ? query(ref, where('permissions.internal_clinical_services', '==', true))
                : query(
                      ref,
                      where('client_id', '==', client.id),
                      where('permissions.engage_management', '==', true)
                  );

            const results = await getDocs(q);
            const usersArray: Array<LocalUserType> = [];
            results.forEach((doc) => {
                const data = { ...doc.data(), id: doc.id } as LocalUserType;
                usersArray.push(data);
            });
            setClinicianList(usersArray);
        })();
    }, [client, userPermissions]);
    // Set default assigned clinician
    React.useEffect(() => {
        if (!clinicianList.length || !loggedInClinician) {
            return;
        }
        formik.setFieldValue('clinician', loggedInClinician.id);
        // eslint-disable-next-line
    }, [clinicianList, loggedInClinician]);

    return (
        <form onSubmit={formik.handleSubmit}>
            <Stack>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        '& > :not(style)': { m: 1 }
                    }}
                >
                    <TextField
                        label="First Name"
                        variant="outlined"
                        fullWidth
                        name="first_name"
                        value={formik.values.first_name}
                        onChange={formik.handleChange}
                        error={formik.touched.first_name && Boolean(formik.errors.first_name)}
                        helperText={formik.touched.first_name && formik.errors.first_name}
                    />
                    <TextField
                        label="Last Name"
                        variant="outlined"
                        fullWidth
                        name="last_name"
                        value={formik.values.last_name}
                        onChange={formik.handleChange}
                        error={formik.touched.last_name && Boolean(formik.errors.last_name)}
                        helperText={formik.touched.last_name && formik.errors.last_name}
                    />
                    <TextField
                        label="Email"
                        variant="outlined"
                        fullWidth
                        name="email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        error={formik.touched.email && Boolean(formik.errors.email)}
                        helperText={formik.touched.email && formik.errors.email}
                    />
                    <PortalDatePicker
                        label="Date Of Birth"
                        date={formik.values.date_of_birth}
                        onDateInputChange={handleDateOfBirthChange}
                        error={
                            formik.touched.date_of_birth && Boolean(formik.errors.date_of_birth)
                                ? { message: formik.errors.date_of_birth }
                                : undefined
                        }
                    />
                    <TextField
                        label="Postcode"
                        variant="outlined"
                        fullWidth
                        name="postcode"
                        value={formik.values.postcode}
                        onChange={formik.handleChange}
                    />
                </Box>
            </Stack>
            <Stack>
                <Box sx={{ display: 'flex', alignItems: 'center', '& > :not(style)': { m: 1 } }}>
                    <TextField
                        multiline
                        rows={4}
                        label="Treatment Notes"
                        fullWidth
                        variant="outlined"
                        name="notes"
                        value={formik.values.notes}
                        onChange={formik.handleChange}
                    />
                </Box>
            </Stack>
            <Stack>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        '& > :not(style)': { m: 1 }
                    }}
                >
                    <FormControl variant="outlined" sx={{ width: 400 }}>
                        <InputLabel htmlFor="programme-select">Programme</InputLabel>
                        <Select
                            variant="outlined"
                            label="Programme"
                            id="programme-select"
                            value={formik.values.programme_id}
                            onChange={formik.handleChange}
                            name="programme_id"
                            error={
                                formik.touched.programme_id && Boolean(formik.errors.programme_id)
                            }
                        >
                            {programmes.map((prog: Programme) => (
                                <MenuItem key={prog.id} value={prog.id}>
                                    {prog.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl variant="outlined" sx={{ width: 400 }}>
                        <InputLabel htmlFor="clinician-assignment">Clinician</InputLabel>
                        <Select
                            variant="outlined"
                            label="clinician"
                            id="clinician-assignment"
                            value={formik.values.clinician}
                            onChange={formik.handleChange}
                            name="clinician"
                            error={formik.touched.clinician && Boolean(formik.errors.clinician)}
                        >
                            <MenuItem value=""></MenuItem>
                            {clinicianList.map((user: LocalUserType) => (
                                <MenuItem key={user.id} value={user.id}>
                                    {`${user.first_name} ${user.last_name}`}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {isInternalClinicalServices && (
                        <CustomerSelect
                            onCustomerSelected={({ customerId, customerName }) => {
                                setEthemeralCustomer({ customerId, customerName });
                            }}
                        />
                    )}
                </Box>
            </Stack>
            <Stack justifyContent="center" alignItems="flex-end">
                <LoadingButton size="large" type="submit" variant="outlined" loading={loading}>
                    Submit
                </LoadingButton>
            </Stack>
        </form>
    );
};

export default DirectReferralForm;
